import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface OtpChallengeProps {
  onVerified: () => void;
}

/**
 * Email-OTP second factor. Shown after a successful password check when the
 * device is not recognized. Reads the pending challenge from AuthContext.
 */
export default function OtpChallenge({ onVerified }: OtpChallengeProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { awaitingOtp, verifyOtp, resendOtp, cancelOtp } = useAuthContext();

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (!awaitingOtp) return null;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError(t('auth.otpInvalidFormat', 'Enter the numeric code from your email.'));
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtp(code.trim(), rememberDevice);
      onVerified();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : t('auth.verifyFailed', 'Verification failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setError('');
    setResending(true);
    try {
      await resendOtp();
      setCooldown(60);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : t('auth.resendFailed', 'Could not resend code.'));
    } finally {
      setResending(false);
    }
  };

  const inputClass = `appearance-none block w-full px-3 py-2 rounded-md shadow-sm text-center tracking-[0.5em] text-lg focus:outline-none focus:ring-blue-500 ${
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
      : 'border-gray-300 focus:border-blue-500'
  }`;

  return (
    <form className="mt-8 space-y-6" onSubmit={handleVerify}>
      <div className="text-center">
        <h2
          className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
        >
          {t('auth.otpTitle', "Verify it's you")}
        </h2>
        <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('auth.otpSentTo', 'We emailed a verification code to')}{' '}
          <strong>{awaitingOtp.maskedEmail}</strong>.
        </p>
      </div>

      {error && (
        <div
          className={`rounded-md p-3 text-sm ${
            theme === 'dark' ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="otp-code"
          className={`block text-sm font-medium ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}
        >
          {t('auth.otpCodeLabel', 'Verification code')}
        </label>
        <input
          id="otp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className={`mt-1 ${inputClass}`}
          placeholder="••••••"
          autoFocus
        />
      </div>

      <label
        className={`flex items-center gap-2 text-sm ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}
      >
        <input
          type="checkbox"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          className="h-4 w-4"
        />
        {t('auth.rememberDevice', 'Trust this device for 30 days')}
      </label>

      <button
        type="submit"
        disabled={submitting}
        className={`w-full flex justify-center py-2 px-4 rounded-md text-sm font-medium text-white ${
          theme === 'dark' ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'
        } ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
      >
        {submitting ? t('auth.verifying', 'Verifying...') : t('auth.verify', 'Verify')}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={cancelOtp}
          className={theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}
        >
          {t('common.back', 'Back')}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
          className={`font-medium ${cooldown > 0 || resending ? 'opacity-50 cursor-not-allowed' : ''} ${
            theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
          }`}
        >
          {cooldown > 0
            ? t('auth.resendIn', { seconds: cooldown, defaultValue: `Resend in ${cooldown}s` })
            : t('auth.resendCode', 'Resend code')}
        </button>
      </div>
    </form>
  );
}
